import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Lazily loads a translation bundle for the given language and namespace.
 * Returns `true` while loading, `false` once translations are ready.
 * Skips loading if the bundle is already present (e.g. loaded by another component).
 * Uses addResourceBundle with deep merge — multiple loads of the same namespace are merged, not overwritten.
 */
export function useLoadLanguage(
  language: string,
  namespace: string,
  loader: (language: string) => Promise<Record<string, string>>,
) {
  const { i18n } = useTranslation()

  const hasBundle = useCallback(() => {
    const bundle = i18n.getResourceBundle(language, namespace)
    return bundle && Object.keys(bundle).length > 0
  }, [i18n, language, namespace])

  const [isLoading, setIsLoading] = useState(!hasBundle())

  useEffect(() => {
    if (hasBundle()) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)

    loader(language)
      .then(resource => {
        const bundle = resource.default ?? resource
        i18n.addResourceBundle(language, namespace, bundle, true, true)
      })
      .finally(() => setIsLoading(false))
  }, [language, namespace, loader, i18n, hasBundle])

  return isLoading
}

/**
 * Creates a loader function that maps short language codes (e.g. 'en') to locale file names
 * (e.g. 'en-US') and dynamically imports the corresponding translation JSON.
 */
export function createLanguageLoader(
  languageToFileMap: Record<string, string>,
  importFn: (file: string) => Promise<Record<string, string>>,
) {
  return (language: string) => {
    let lang = languageToFileMap[language]
    if (!lang) {
      console.error(`Language "${language}" not found. Fallback to english.`)
      lang = 'en-US'
    }
    return importFn(lang)
  }
}
